import React, { useState } from 'react';
import Layout from '../components/Layout';
import MobileLayout from '../components/Mobile/MobileLayout';
import { useIsMobile } from '../hooks/useIsMobile';
import { Container, Card, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { Spinner } from 'react-bootstrap';

const ContactUs: React.FC = () => {
  const isMobile = useIsMobile();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Simulate form submission
    setTimeout(() => {
      setSubmitted(true);
      setLoading(false);
      setFormData({ name: '', email: '', subject: '', message: '' });
    }, 1000);
  };

  const content = (
    <Container className="py-4">
      <Card className="shadow-sm overflow-hidden">
        {/* Header */}
        <div className="bg-primary text-white text-center p-5" style={{background: 'linear-gradient(to right, #0d6efd, #4c2aa9)'}}>
          <h1 className="mb-2">Contact Us</h1>
          <p className="text-white text-opacity-75">
            We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>

        <Card.Body className="p-4 p-md-5">
          <Row>
            {/* Contact Information */}
            <Col lg={4} className="mb-4 mb-lg-0">
              <div className="mb-4">
                <h2 className="mb-3">Get in Touch</h2>
                <p className="text-muted small mb-4">
                  Have questions, feedback, or need support? Reach out to us through any of these channels.
                </p>
              </div>

              {/* Email */}
              <div className="d-flex gap-3 mb-4">
                <div className="flex-shrink-0 bg-primary bg-opacity-10 rounded d-flex align-items-center justify-content-center" style={{width: '48px', height: '48px'}}>
                  <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" className="text-primary">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="h6 fw-bold mb-0">Email</h3>
                  <a
                    href="mailto:support@nayabajar.com"
                    className="text-primary small"
                  >
                    support@nayabajar.com
                  </a>
                  <p className="text-muted small mb-0 mt-1">We reply within 24 hours</p>
                </div>
              </div>

              {/* Phone */}
              <div className="d-flex gap-3 mb-4">
                <div className="flex-shrink-0 bg-success bg-opacity-10 rounded d-flex align-items-center justify-content-center" style={{width: '48px', height: '48px'}}>
                  <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" className="text-success">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="h6 fw-bold mb-0">Phone</h3>
                  <a
                    href="tel:+9779800000000"
                    className="text-secondary small text-decoration-none"
                  >
                    +977 9800000000
                  </a>
                  <p className="text-muted small mb-0 mt-1">Sun - Fri, 10 AM - 6 PM</p>
                </div>
              </div>

              {/* Address */}
              <div className="d-flex gap-3 mb-4">
                <div className="flex-shrink-0 rounded d-flex align-items-center justify-content-center" style={{width: '48px', height: '48px', backgroundColor: 'rgba(111, 66, 193, 0.1)'}}>
                  <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{color: '#6f42c1'}}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="h6 fw-bold mb-0">Address</h3>
                  <p className="small mb-0">
                    Kathmandu, Nepal
                  </p>
                </div>
              </div>

              {/* Live Chat */}
              <div className="d-flex gap-3">
                <div className="flex-shrink-0 bg-warning bg-opacity-10 rounded d-flex align-items-center justify-content-center" style={{width: '48px', height: '48px'}}>
                  <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24" className="text-warning">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <div>
                  <h3 className="h6 fw-bold mb-0">Live Chat</h3>
                  <p className="small mb-0">
                    Available on the bottom right of any page
                  </p>
                  <p className="text-muted small mb-0 mt-1">Average response: 5 minutes</p>
                </div>
              </div>
            </Col>

            {/* Contact Form */}
            <Col lg={8}>
              {submitted ? (
                <Alert variant="success" className="text-center p-4">
                  <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{width: '64px', height: '64px'}}>
                    <svg width="32" height="32" fill="none" stroke="currentColor" viewBox="0 0 24 24" className="text-success">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="fw-bold mb-2">Message Sent!</h3>
                  <p className="mb-3">
                    Thank you for contacting us. We've received your message and will get back to you within 24 hours.
                  </p>
                  <Button
                    variant="success"
                    onClick={() => setSubmitted(false)}
                  >
                    Send Another Message
                  </Button>
                </Alert>
              ) : (
                <Form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <h2 className="mb-3">Send us a Message</h2>
                    <p className="text-muted small">
                      Fill out the form below and we'll get back to you as soon as possible.
                    </p>
                  </div>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="small">Name *</Form.Label>
                        <Form.Control
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Your full name"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label className="small">Email *</Form.Label>
                        <Form.Control
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="your@email.com"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label className="small">Subject *</Form.Label>
                    <Form.Select
                      required
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    >
                      <option value="">Select a topic</option>
                      <option value="general">General Inquiry</option>
                      <option value="support">Technical Support</option>
                      <option value="billing">Billing & Payments</option>
                      <option value="report">Report a Problem</option>
                      <option value="feedback">Feedback & Suggestions</option>
                      <option value="partnership">Partnership Inquiry</option>
                      <option value="other">Other</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label className="small">Message *</Form.Label>
                    <Form.Control
                      as="textarea"
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={6}
                      placeholder="Describe your inquiry in detail..."
                    />
                  </Form.Group>

                  <Button
                    variant="primary"
                    type="submit"
                    disabled={loading}
                    className="w-100 w-md-auto"
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" className="me-2" style={{verticalAlign: 'text-bottom'}}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                        Send Message
                      </>
                    )}
                  </Button>
                </Form>
              )}
            </Col>
          </Row>

          {/* FAQ Link */}
          <div className="mt-5 pt-4 border-top">
            <Card bg="light" className="border-0 text-center p-4">
              <h3 className="h6 fw-bold mb-2">
                Before you reach out...
              </h3>
              <p className="text-muted small mb-3">
                Check our FAQ section for quick answers to common questions.
              </p>
              <Button
                variant="outline-primary"
                href="/faq"
              >
                View FAQ
              </Button>
            </Card>
          </div>

          {/* Quick Contact Options */}
          <Row className="mt-4 g-3">
            <Col xs={6} md={3}>
              <a
                href="/faq"
                className="card text-center text-decoration-none h-100 border-0 bg-light hover-bg-gray-100"
              >
                <Card.Body className="p-3">
                  <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2" style={{width: '40px', height: '40px'}}>
                    <span className="fs-5">❓</span>
                  </div>
                  <span className="small fw-bold text-secondary">FAQ</span>
                </Card.Body>
              </a>
            </Col>

            <Col xs={6} md={3}>
              <a
                href="/safety-tips"
                className="card text-center text-decoration-none h-100 border-0 bg-light hover-bg-gray-100"
              >
                <Card.Body className="p-3">
                  <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2" style={{width: '40px', height: '40px'}}>
                    <span className="fs-5">🛡️</span>
                  </div>
                  <span className="small fw-bold text-secondary">Safety Tips</span>
                </Card.Body>
              </a>
            </Col>

            <Col xs={6} md={3}>
              <a
                href="/posting-rules"
                className="card text-center text-decoration-none h-100 border-0 bg-light hover-bg-gray-100"
              >
                <Card.Body className="p-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2" style={{width: '40px', height: '40px', backgroundColor: 'rgba(111, 66, 193, 0.1)'}}>
                    <span className="fs-5">📋</span>
                  </div>
                  <span className="small fw-bold text-secondary">Rules</span>
                </Card.Body>
              </a>
            </Col>

            <Col xs={6} md={3}>
              <a
                href="/pricing"
                className="card text-center text-decoration-none h-100 border-0 bg-light hover-bg-gray-100"
              >
                <Card.Body className="p-3">
                  <div className="bg-warning bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mx-auto mb-2" style={{width: '40px', height: '40px'}}>
                    <span className="fs-5">💎</span>
                  </div>
                  <span className="small fw-bold text-secondary">Pricing</span>
                </Card.Body>
              </a>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  );

  return isMobile ? <MobileLayout>{content}</MobileLayout> : <Layout showSidebar={false}>{content}</Layout>;
};

export default ContactUs;
